/**
 * Feature E03-F04 — "Генерация пакета документов" request/response + content
 * contracts. 1:1 port of the legacy n8n Gen / SS / KS / FU / GDOCX sub-flows
 * (docs/source-n8n/reference/{Gen,SS,KS,FU,GDOCX}.md) and the canonical prompts
 * `generate_documents`, `generate_kickoff_sheet`, `generate_followup_email`,
 * `regenerate_document` (docs/source-n8n/ai_prompts.md).
 *
 * Single source of truth for:
 *   - POST /projects/:id/ai/generate_documents     (E03-F04-T04, T01, T01-S03)
 *   - POST /projects/:id/ai/generate_kickoff_sheet  (E03-F04-T04, T02, T02-S01)
 *   - POST /projects/:id/ai/generate_followup_email (E03-F04-T03, T04 fast-track)
 *   - the artifact `content` shapes UPSERTed into project_artifacts by each
 *     pipeline (jd_doc / interview_questions / inmails / sourcing_sheet /
 *     kickoff_sheet / client_followup_email)
 *   - the Stage-3 Generation UI constants the SPA renders (E03-F04-T07):
 *     GENERATION_ORDER, ARTIFACT_STATUS_BADGES, the per-item action map.
 *
 * IDOR invariant (carried over unchanged from every legacy gotcha): `userId` /
 * `created_by` is NEVER part of any *input* schema here — the api derives the
 * actor from the validated session (`request.user`), so a request can never
 * forge it. Only `generation_notes` (the AI-instructions textarea) is an input.
 *
 * Lives in its OWN file; the serial Wire pass adds the `export *` line to
 * `packages/shared/src/index.ts` (this agent must NOT edit the barrel).
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Artifact types (project_artifacts.type) — the 6 documents this feature owns
// ---------------------------------------------------------------------------
/** The 4 documents produced by the single `generate_documents` call (Gen.md). */
export const GENERATE_DOCUMENTS_TYPES = [
    'jd_doc',
    'interview_questions',
    'inmails',
    'sourcing_sheet',
];
/** Kickoff Sheet artifact type (KS.md). */
export const KICKOFF_SHEET_TYPE = 'kickoff_sheet';
/** Sourcing Sheet artifact type (SS.md) — also emitted by generate_documents. */
export const SOURCING_SHEET_TYPE = 'sourcing_sheet';
/** Follow-up email artifact type (FU.md). */
export const CLIENT_FOLLOWUP_EMAIL_TYPE = 'client_followup_email';
/** Every artifact type this feature can create/own. */
export const DOC_GENERATION_ARTIFACT_TYPES = [
    ...GENERATE_DOCUMENTS_TYPES,
    KICKOFF_SHEET_TYPE,
    CLIENT_FOLLOWUP_EMAIL_TYPE,
];
/**
 * Confidentiality placeholder substituted for `{{clientName}}` in the
 * generate_documents prompt so the client company name NEVER leaks into the JD,
 * InMails or Interview Questions (Gen.md gotcha; E03-F04-T05). The Sourcing
 * Sheet is internal-only and DOES receive the real client name.
 */
export const CONFIDENTIAL_CLIENT_PLACEHOLDER = '[CONFIDENTIAL - do not mention in any document text]';
/**
 * Confidential marker used by regenerate_document for jd_doc / inmails /
 * interview_questions when the project is run in confidential mode
 * (E03-F04-T05 acceptance #3).
 */
export const CONFIDENTIAL_REGENERATE_PLACEHOLDER = '[CONFIDENTIAL - do not mention in document text]';
// ---------------------------------------------------------------------------
// Document content shapes (project_artifacts.content) — generate_documents
// (ai_prompts.md → generate_documents OUTPUT FORMAT)
// ---------------------------------------------------------------------------
/** JD position-title block (main + alternatives for sourcing discoverability). */
export const jdPositionTitleBlockSchema = z.object({
    main_title: z.string().default(''),
    alternative_titles: z.array(z.string()).default([]),
});
/**
 * Job Description content (jd_doc). About Company starts with "We are helping
 * our client…" and NEVER names the company; What Company Offers carries no
 * specific salary numbers (E03-F04-T05). Every section has a sibling
 * `_comments` array (internal-only market insight / gaps).
 */
export const jdDocContentSchema = z
    .object({
    position_title_block: jdPositionTitleBlockSchema.optional(),
    about_company: z.string().optional(),
    about_company_comments: z.array(z.string()).optional(),
    about_role: z.string().optional(),
    about_role_comments: z.array(z.string()).optional(),
    responsibilities: z.array(z.string()).optional(),
    responsibilities_comments: z.array(z.string()).optional(),
    requirements: z.array(z.string()).optional(),
    requirements_comments: z.array(z.string()).optional(),
    company_offers: z.string().optional(),
    company_offers_comments: z.array(z.string()).optional(),
})
    .passthrough();
/**
 * Interview Questions content. Canonical buckets are technical / behavioral /
 * culture_fit / client_specific (ai_prompts.md), but the legacy GDOCX builder
 * also rendered situational / motivational and a flat `questions[]` fallback —
 * passthrough preserves whatever the model returns.
 */
export const interviewQuestionsContentSchema = z
    .object({
    technical: z.array(z.string()).optional(),
    behavioral: z.array(z.string()).optional(),
    culture_fit: z.array(z.string()).optional(),
    client_specific: z.array(z.string()).optional(),
    situational: z.array(z.string()).optional(),
    motivational: z.array(z.string()).optional(),
    questions: z.array(z.string()).optional(),
})
    .passthrough();
/** One InMail variant: tone + subject + body (closing comes from the prompt, not code). */
export const inmailVariantSchema = z
    .object({
    tone: z.string().optional(),
    label: z.string().optional(),
    subject: z.string().optional(),
    body: z.string().default(''),
})
    .passthrough();
/** InMails content — 3 variants (no code-appended signature; the prompt owns the closing). */
export const inmailsContentSchema = z
    .object({
    variants: z.array(inmailVariantSchema).default([]),
})
    .passthrough();
/** Sourcing Sheet content (ai_prompts.md → generate_sourcing_sheet OUTPUT). */
export const sourcingSheetContentSchema = z.record(z.unknown());
// ---------------------------------------------------------------------------
// Kickoff Sheet content (KS.md / ai_prompts.md → generate_kickoff_sheet)
// ---------------------------------------------------------------------------
/**
 * Kickoff Sheet content. The prompt returns `{ kickoff_sheet: "<markdown>" }`
 * (plain-markdown variant). KS: Parse & Save then appends `sourcing_sheet`
 * (the SS artifact just generated) and `jd_copy` (original client JD from
 * manual_context files/links). T02 acceptance: support BOTH the structured
 * field form and the plain markdown form — hence passthrough + optional fields.
 */
export const kickoffSheetContentSchema = z
    .object({
    /** Plain-markdown body (the prompt's `kickoff_sheet` key). */
    kickoff_sheet: z.string().optional(),
    /** Alternate plain-text key some variants use. */
    text: z.string().optional(),
    /** Appended: the project's sourcing_sheet artifact content (KS.md step 3). */
    sourcing_sheet: z.unknown().optional(),
    /** Appended: original client JD — file links and/or referenced-link text (KS.md step 4). */
    jd_copy: z.string().optional(),
})
    .passthrough();
// ---------------------------------------------------------------------------
// Follow-up email content (FU.md → client_followup_email)
// ---------------------------------------------------------------------------
/** Follow-up email content: {subject, body, missing_items} (FU: Parse & Save). */
export const followupEmailContentSchema = z
    .object({
    subject: z.string().default(''),
    body: z.string().default(''),
    missing_items: z.array(z.string()).default([]),
})
    .passthrough();
// ---------------------------------------------------------------------------
// Request schema (shared by all three AI actions) — only generation_notes
// ---------------------------------------------------------------------------
/**
 * Body for every generation action. The ONLY input is the optional
 * AI-instructions textarea (`generation_notes`). projectId is the `:id` path
 * param; the actor is the session — never the body (IDOR rule).
 */
export const docGenerationRequestSchema = z.object({
    generation_notes: z.string().optional(),
});
// ---------------------------------------------------------------------------
// Response schemas (one per action) — what the SPA renders
// ---------------------------------------------------------------------------
/** Response of POST …/ai/generate_documents (Gen: Return Result — no docs in body). */
export const generateDocumentsResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    message: z.string().default('Documents generated'),
    projectId: z.number().int(),
    /** Which of the 4 docs were actually parsed & saved (UI counter aid). */
    generated: z.array(z.enum(GENERATE_DOCUMENTS_TYPES)).default([]),
});
/** Response of POST …/ai/generate_kickoff_sheet (KS: Return Result). */
export const generateKickoffSheetResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    message: z.string().default('Kickoff Sheet generated'),
    projectId: z.number().int(),
    /** The composed kickoff sheet (body + nested sourcing_sheet + jd_copy). */
    kickoff_sheet: kickoffSheetContentSchema,
});
/**
 * Response of POST …/ai/generate_sourcing_sheet (SS: Return Result, E03-F04-T01-S02
 * / T02-S02). SS is a STANDALONE LLM pipeline that produces only the
 * `sourcing_sheet` artifact (separate from the generate_documents batch — in the
 * legacy n8n flow SS ran as its own sub-flow chained into KS).
 */
export const generateSourcingSheetResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    message: z.string().default('Sourcing Sheet generated'),
    projectId: z.number().int(),
    /** The parsed sourcing_sheet content (empty object when the LLM answer did not parse). */
    sourcing_sheet: sourcingSheetContentSchema,
});
/** Response of POST …/ai/generate_followup_email (FU: Return Result). */
export const generateFollowupEmailResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    message: z.string().default('Follow-up email generated'),
    email_content: followupEmailContentSchema,
});
// ---------------------------------------------------------------------------
// UI constants — Stage-3 Generation (E03-F04-T07; legacy _renderGenCard)
// ---------------------------------------------------------------------------
/**
 * The fixed render order of the generation cards (legacy GENERATION_ORDER).
 * Kickoff Sheet and Follow-up Email are "special items" generated through their
 * OWN ai action; the three plain docs (JD, InMails, Interview Questions) are
 * (re)generated through `regenerate_document` after the first `generate_documents`
 * batch. `internal_notification` is rendered as its own block at the end.
 */
export const GENERATION_ORDER = [
    'kickoff_sheet',
    'client_followup_email',
    'jd_doc',
    'inmails',
    'interview_questions',
];
/** Human label per generation card. */
export const GENERATION_LABELS = {
    kickoff_sheet: 'Kickoff Sheet',
    client_followup_email: 'Follow-up Email',
    jd_doc: 'Job Description',
    inmails: 'InMails',
    interview_questions: 'Interview Questions',
    // E03-F07-T07 — user-facing rename (Ksenia 06.10); the internal key stays.
    internal_notification: 'Kickoff done mail',
    sourcing_sheet: 'Sourcing Sheet',
};
/**
 * The AI action a card invokes for its FIRST generation. Special items use their
 * dedicated endpoint; plain docs are part of the generate_documents batch and
 * regenerate through regenerate_document afterwards (T07 acceptance #1).
 */
export const GENERATION_FIRST_ACTION = {
    kickoff_sheet: 'generate_kickoff_sheet',
    client_followup_email: 'generate_followup_email',
    jd_doc: 'generate_documents',
    inmails: 'generate_documents',
    interview_questions: 'generate_documents',
};
/**
 * Communication items — eligible for "Send" (vs Download for non-comm docs).
 * PK-QA-27: `inmails` was removed — it is NOT sent directly (like Interview
 * Questions it is copied/downloaded, never emailed). The ONLY directly-sendable
 * generation doc is `client_followup_email`; this predicate is now the single
 * source of truth for "sendability" consumed by both the card-level actions
 * (`cardActionsFor`) and the editor's `fastTrack` gate (`isCommunicationItem`).
 */
export const COMMUNICATION_ITEMS = ['client_followup_email'];
/**
 * Doc types whose Generation card exposes a "Send" button (E05-F06-T09).
 *
 * A SUPERSET of {@link COMMUNICATION_ITEMS}, and deliberately a SEPARATE list.
 * "Communication item" carries two other meanings in `cardActionsFor`: such a doc
 * is NOT downloadable, and the editor opens with the fast-track gate. The Sourcing
 * Sheet is a real package deliverable — it must keep Download and stay inside the
 * PK-QA-31 confirm gate — yet the sourcing team has to receive it by email exactly
 * the way the Kickoff Sheet does (Ксения: «у Sourcing Sheet нет кнопки отправки»).
 * Folding it into COMMUNICATION_ITEMS would have silently taken its Download button
 * away, so sendability gets its own predicate instead.
 */
export const SENDABLE_DOC_TYPES = [...COMMUNICATION_ITEMS, SOURCING_SHEET_TYPE];
/** True when this doc type can be emailed from its Generation card. */
export function isSendableDoc(type) {
    return SENDABLE_DOC_TYPES.includes(type);
}
/**
 * True when this doc type is a COMMUNICATION item — a prose message rather than a
 * package deliverable. Distinct from {@link isSendableDoc} on purpose: this one
 * additionally means "not downloadable" and "editor opens fast-tracked", which is
 * exactly why the Sourcing Sheet must be sendable WITHOUT being a comm item.
 *
 * Lives here (not in `@eer/web`) so the two predicates that decide the Send /
 * Download pair sit side by side and cannot drift; `docGenerationModel` re-exports
 * it for its existing callers.
 */
export function isCommunicationItem(type) {
    return COMMUNICATION_ITEMS.includes(type);
}
/** Artifact lifecycle statuses → badge label + tone (T07 acceptance #2). */
export const ARTIFACT_STATUSES = [
    'not_generated',
    'draft',
    'generated',
    'edited',
    'approved',
    'sent',
];
/** Badge text + visual tone per status (approved/sent are highlighted). */
export const ARTIFACT_STATUS_BADGES = {
    not_generated: { label: 'Not generated', tone: 'neutral' },
    draft: { label: 'Draft', tone: 'info' },
    generated: { label: 'Generated', tone: 'info' },
    edited: { label: 'Edited', tone: 'info' },
    approved: { label: 'Approved', tone: 'highlight' },
    sent: { label: 'Sent', tone: 'success' },
};
/** Step labels shown by the "Generate All Docs" spinner (T07 acceptance #3). */
export const GENERATE_ALL_STEPS = [
    { action: 'generate_kickoff_sheet', label: 'Generating kickoff sheet…' },
    { action: 'generate_documents', label: 'Generating documents…' },
    { action: 'generate_followup_email', label: 'Generating follow-up email…' },
];
//# sourceMappingURL=doc-generation.js.map